import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, BACKEND_URL } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
  Plus, Trash2, Star, StarOff, ArrowUp, ArrowDown, Video, ExternalLink,
} from "lucide-react";

const resolveVideo = (url) =>
  url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url;

export default function AdminIntroVideos() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [artists, setArtists] = useState([]);
  const [newArtistId, setNewArtistId] = useState("");
  const [newVideoUrl, setNewVideoUrl] = useState("");
  const [newTitle, setNewTitle] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/intro-videos");
      setRows(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load intro videos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api
      .get("/admin/users", { params: { role: "artist", limit: 200 } })
      .then(({ data }) => setArtists((data.items || []).filter((u) => !u.deleted_at)))
      .catch(() => {});
  }, []);

  const selectedArtist = artists.find((a) => a.id === newArtistId);

  // Inline video upload — replaces a trip through the journalist submission
  // portal. File goes through /api/upload/video (admin role, 100 MB cap)
  // and the resulting /api/files/<id> URL drops straight into the add form.
  const handleInlineVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: r } = await api.post("/upload/video", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setNewVideoUrl(r.url);
      toast.success("Video uploaded · paste-ready URL");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submitNew = async (e) => {
    e.preventDefault();
    if (!newArtistId) {
      toast.error("Pick an artist");
      return;
    }
    setAdding(true);
    try {
      const payload = { artist_id: newArtistId };
      if (newVideoUrl.trim()) payload.video_url = newVideoUrl.trim();
      if (newTitle.trim()) payload.title = newTitle.trim();
      await api.post("/admin/intro-videos", payload);
      toast.success("Intro video added");
      setNewArtistId("");
      setNewVideoUrl("");
      setNewTitle("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add intro video");
    } finally {
      setAdding(false);
    }
  };

  const toggleFeature = async (row) => {
    try {
      await api.patch(`/admin/intro-videos/${row.id}`, { is_featured: !row.is_featured });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    }
  };

  const removeRow = async (row) => {
    if (!window.confirm(`Remove ${row.artist_name}'s intro video from the homepage gallery?`)) return;
    try {
      await api.delete(`/admin/intro-videos/${row.id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  const move = async (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= rows.length) return;
    const reordered = [...rows];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(target, 0, moved);
    setRows(reordered);
    try {
      await api.post("/admin/intro-videos/reorder", { order: reordered.map((r) => r.id) });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reorder failed");
      load();
    }
  };

  return (
    <div data-testid="admin-intro-videos" className="pb-16">
      <p className="label-eyebrow text-[#1E3A8A]">— Homepage curation</p>
      <h1 className="font-serif-display text-5xl tracking-tighter font-medium mt-3 text-[#111]">
        Introduction videos
      </h1>
      <p className="text-[#555] mt-3 max-w-2xl">
        Curate the &ldquo;Meet the masters&rdquo; gallery on the homepage. Up to 4 featured videos
        appear in the editorial carousel; the first one plays as the hero.
      </p>

      <form
        onSubmit={submitNew}
        data-testid="intro-video-add-form"
        className="mt-10 border border-[#E5E0D8] bg-white p-6 grid grid-cols-1 lg:grid-cols-12 gap-4"
      >
        <div className="lg:col-span-4">
          <label className="label-eyebrow text-[#555]">Artist</label>
          <Select value={newArtistId} onValueChange={setNewArtistId}>
            <SelectTrigger
              data-testid="intro-video-artist-select"
              className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus:ring-0"
            >
              <SelectValue placeholder="Pick an artist…" />
            </SelectTrigger>
            <SelectContent className="rounded-none max-h-[300px]">
              {artists.map((a) => (
                <SelectItem
                  key={a.id}
                  value={a.id}
                  data-testid={`intro-video-artist-option-${a.id}`}
                >
                  {a.name} {a.publish_status === "published" ? "" : "· (draft)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedArtist?.intro_video_url && (
            <p className="text-xs text-[#1B5E20] mt-2 truncate">
              ✓ Profile has a video — leave URL blank to reuse it.
            </p>
          )}
        </div>
        <div className="lg:col-span-4">
          <label className="label-eyebrow text-[#555]">Video URL (override)</label>
          <Input
            data-testid="intro-video-url-input"
            value={newVideoUrl}
            onChange={(e) => setNewVideoUrl(e.target.value)}
            placeholder="/api/files/… or https://…"
            className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#111]"
          />
          <div className="mt-2 flex items-center gap-2">
            <label
              data-testid="intro-video-upload-label"
              className="label-eyebrow text-[#1E3A8A] hover:text-[#111] cursor-pointer inline-flex items-center gap-1"
              title="Upload an MP4/WebM directly (up to 100 MB)"
            >
              <input
                data-testid="intro-video-upload-file"
                type="file"
                accept="video/mp4,video/webm,video/quicktime,video/x-m4v"
                onChange={handleInlineVideoUpload}
                disabled={uploading}
                className="hidden"
              />
              <Plus className="w-3 h-3" />
              {uploading ? "Uploading…" : "Upload file"}
            </label>
            {newVideoUrl && newVideoUrl.startsWith("/api/files/") && (
              <span className="label-eyebrow text-[#1B5E20]">✓ Hosted</span>
            )}
          </div>
        </div>
        <div className="lg:col-span-2">
          <label className="label-eyebrow text-[#555]">Title (optional)</label>
          <Input
            data-testid="intro-video-title-input"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Headline override"
            className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#111]"
          />
        </div>
        <div className="lg:col-span-2 flex items-end">
          <Button
            type="submit"
            disabled={adding || !newArtistId}
            data-testid="intro-video-add-btn"
            className="w-full rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white py-3 h-auto label-eyebrow disabled:opacity-40"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            {adding ? "Adding…" : "Add"}
          </Button>
        </div>
      </form>

      <div className="mt-10">
        {loading ? (
          <p className="label-eyebrow text-[#555]">Loading…</p>
        ) : rows.length === 0 ? (
          <div
            data-testid="intro-video-empty"
            className="border border-dashed border-[#E5E0D8] p-12 text-center"
          >
            <Video className="w-8 h-8 text-[#1E3A8A] mx-auto" strokeWidth={1.5} />
            <p className="font-serif-display text-2xl mt-4 text-[#111]">
              No intro videos curated yet
            </p>
            <p className="text-sm text-[#555] mt-2">
              Add a video above. The homepage section will appear automatically once one is featured.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-[#E5E0D8] border border-[#E5E0D8] bg-white">
            {rows.map((row, idx) => (
              <li
                key={row.id}
                data-testid={`intro-video-row-${row.id}`}
                className="p-4 flex items-center gap-4"
              >
                <span className="font-mono-aam text-2xl text-[#1E3A8A] w-10 flex-shrink-0">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                {row.artist_avatar_url ? (
                  <img
                    src={
                      row.artist_avatar_url.startsWith("/api")
                        ? `${BACKEND_URL}${row.artist_avatar_url}`
                        : row.artist_avatar_url
                    }
                    alt=""
                    className="w-14 h-14 object-cover border border-[#E5E0D8] flex-shrink-0"
                  />
                ) : (
                  <div className="w-14 h-14 bg-[#F4EFE6] border border-[#E5E0D8] flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-serif-display text-lg text-[#111] truncate">
                    {row.artist_name}
                  </p>
                  <p className="label-eyebrow text-[#555] mt-0.5 truncate">
                    {row.title}
                  </p>
                  {row.video_url ? (
                    <a
                      href={resolveVideo(row.video_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#1E3A8A] hover:underline flex items-center gap-1 mt-1 truncate"
                    >
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{row.video_url}</span>
                    </a>
                  ) : (
                    <p className="text-xs text-[#D32F2F] mt-1">No video URL — assign one in artist profile</p>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    data-testid={`intro-video-up-${row.id}`}
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    className="p-2 hover:bg-[#F4EFE6]/60 disabled:opacity-30"
                    title="Move up"
                  >
                    <ArrowUp className="w-4 h-4 text-[#1E3A8A]" />
                  </button>
                  <button
                    type="button"
                    data-testid={`intro-video-down-${row.id}`}
                    onClick={() => move(idx, 1)}
                    disabled={idx === rows.length - 1}
                    className="p-2 hover:bg-[#F4EFE6]/60 disabled:opacity-30"
                    title="Move down"
                  >
                    <ArrowDown className="w-4 h-4 text-[#1E3A8A]" />
                  </button>
                  <button
                    type="button"
                    data-testid={`intro-video-feature-${row.id}`}
                    onClick={() => toggleFeature(row)}
                    className={`p-2 hover:bg-[#F4EFE6]/60 ${row.is_featured ? "text-[#A87900]" : "text-[#555]"}`}
                    title={row.is_featured ? "Featured · click to unfeature" : "Not featured · click to feature"}
                  >
                    {row.is_featured ? (
                      <Star className="w-4 h-4" fill="currentColor" />
                    ) : (
                      <StarOff className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    type="button"
                    data-testid={`intro-video-delete-${row.id}`}
                    onClick={() => removeRow(row)}
                    className="p-2 hover:bg-[#D32F2F]/10 text-[#D32F2F]"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
